/**
 * Helpers purs du suivi de stock (Food aujourd'hui, Produits additionnels en Lot B).
 * Convention : `null` = stock infini (∞). Aucune règle métier ici : le serveur fait foi,
 * ces helpers ne servent qu'à l'affichage.
 */
import moment from 'moment'
import 'moment/locale/fr'

export const INFINITY = '∞'
export const MINUS = '−' // U+2212, jamais le tiret-moins ASCII à l'affichage

export const STOCK_COLORS = {
    infinite: '#6b7280',
    danger: '#911414',
    warning: '#d97706',
    normal: '#4b5563',
}

/**
 * Ton d'affichage d'une valeur de stock (seuils de la maquette PO).
 * null → ∞ gris ; ≤ 0 rouge (négatif compris) ; 1–5 orange ; > 5 gris.
 * @param {number|null|undefined} value
 * @returns {{ key: 'infinite'|'danger'|'warning'|'normal', color: string }}
 */
export function stockTone(value) {
    if (value === null || value === undefined) return { key: 'infinite', color: STOCK_COLORS.infinite }
    const n = Number(value)
    if (Number.isNaN(n) || n <= 0) return { key: 'danger', color: STOCK_COLORS.danger }
    if (n <= 5) return { key: 'warning', color: STOCK_COLORS.warning }
    return { key: 'normal', color: STOCK_COLORS.normal }
}

/**
 * Valeur de stock affichable : « ∞ » ou le nombre brut (négatif conservé).
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatStock(value) {
    return value === null || value === undefined ? INFINITY : String(value)
}

/**
 * Libellé signé d'un delta : `+3` / `−2` (U+2212) / `0`.
 * @param {number} delta
 * @returns {string}
 */
export function formatDelta(delta) {
    const n = Number(delta) || 0
    if (n > 0) return `+${n}`
    if (n < 0) return `${MINUS}${Math.abs(n)}`
    return '0'
}

/**
 * Libellé de variation entre deux valeurs : `+X` / `−X`, ou `∞ → 12` / `12 → ∞` si un côté est infini.
 * Retourne '' si rien ne change.
 * @param {number|null} from
 * @param {number|null} to
 * @returns {string}
 */
export function variationLabel(from, to) {
    const f = from === undefined ? null : from
    const t = to === undefined ? null : to
    if (f === t) return ''
    if (f === null || t === null) return `${formatStock(f)} → ${formatStock(t)}`
    return formatDelta(Number(t) - Number(f))
}

/**
 * Infobulle « 12 → 9 » d'une variation.
 * @param {number|null} from
 * @param {number|null} to
 * @returns {string}
 */
export function variationTitle(from, to) {
    return `${formatStock(from)} → ${formatStock(to)}`
}

/**
 * Cumul d'un lot de modifications `{ from, to }`.
 * N→∞ est compté à part (`infinityCount`) ; ∞→N entre dans `deltaSum` (+N), comme la convention serveur
 * `delta = (after ?? 0) − (before ?? 0)`.
 * @param {Array<{from: number|null, to: number|null}>} changes
 * @returns {{ count: number, deltaSum: number, infinityCount: number }}
 */
export function summarizeStockChanges(changes) {
    const summary = { count: 0, deltaSum: 0, infinityCount: 0 }
    ;(changes || []).forEach((change) => {
        if (!change) return
        const from = change.from === undefined ? null : change.from
        const to = change.to === undefined ? null : change.to
        if (from === to) return
        summary.count += 1
        if (to === null) {
            summary.infinityCount += 1
            return
        }
        summary.deltaSum += Number(to) - (from === null ? 0 : Number(from))
    })
    return summary
}

/**
 * « 3 produits modifiés (−12, 1 × ∞) ».
 * @param {{ count: number, deltaSum: number, infinityCount: number }} summary
 * @param {Function} t - `this.$t`
 * @returns {string}
 */
export function formatBulkSummary(summary, t) {
    if (!summary || !summary.count) return ''
    const head = t(summary.count > 1 ? 'stock.bulk.modified_other' : 'stock.bulk.modified_one', { count: summary.count })
    const parts = []
    if (summary.deltaSum !== 0) parts.push(formatDelta(summary.deltaSum))
    if (summary.infinityCount > 0) parts.push(t('stock.bulk.infinity_part', { count: summary.infinityCount }))
    return parts.length ? `${head} (${parts.join(', ')})` : head
}

const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * @param {Date|moment.Moment|string} date
 * @returns {string} `YYYY-MM-DD`
 */
export function toDateString(date) {
    return moment(date).format(DATE_FORMAT)
}

function capitalize(label) {
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : label
}

/**
 * Libellé complet d'une période, jamais abrégé :
 * - un jour : « Mardi 6 octobre 2026 »
 * - une plage : « Du 11 au 15 août 2026 », « Du 28 septembre au 3 octobre 2026 »,
 *   « Du 28 décembre 2025 au 3 janvier 2026 ».
 * @param {string} from - `YYYY-MM-DD`
 * @param {string} to - `YYYY-MM-DD`
 * @param {{ locale?: string, t?: Function }} [options]
 * @returns {string}
 */
export function formatPeriodLabel(from, to, options = {}) {
    const locale = options.locale || 'fr'
    const start = moment(from, DATE_FORMAT).locale(locale)
    const end = moment(to || from, DATE_FORMAT).locale(locale)
    if (!start.isValid()) return ''
    if (!end.isValid() || start.isSame(end, 'day')) {
        return capitalize(start.format('dddd D MMMM YYYY'))
    }
    let fromLabel
    if (start.isSame(end, 'month')) {
        fromLabel = start.format('D')
    } else if (start.isSame(end, 'year')) {
        fromLabel = start.format('D MMMM')
    } else {
        fromLabel = start.format('D MMMM YYYY')
    }
    const toLabel = end.format('D MMMM YYYY')
    if (typeof options.t === 'function') {
        return options.t('stock.period.range', { from: fromLabel, to: toLabel })
    }
    return `${fromLabel} → ${toLabel}`
}

export const MOVEMENT_TYPES = ['sale', 'free', 'adjustment', 'cancel']

const MOVEMENT_META = {
    sale: { key: 'sale', bg: '#ecdcfa', color: '#571f84' },
    free: { key: 'free', bg: '#dcfce7', color: '#15803d' },
    adjustment: { key: 'adjustment', bg: '#dbeafe', color: '#1d4ed8' },
    cancel: { key: 'cancel', bg: '#fee2e2', color: '#b91c1c' },
}

/**
 * Couleurs du badge d'un type de mouvement (`sale` violet, `free` vert, `adjustment` bleu, `cancel` rouge).
 * @param {string} type
 * @returns {{ key: string, bg: string, color: string, labelKey: string }}
 */
export function movementTypeMeta(type) {
    const meta = MOVEMENT_META[type] || { key: type || 'unknown', bg: '#f3f4f6', color: '#4b5563' }
    return { ...meta, labelKey: `stock.movements.types.${meta.key}` }
}

export const PERIOD_PRESETS = ['today', 'yesterday', 'last_7_days', 'this_week', 'this_month']

/** Période par défaut de la page Mouvements (demande du 2026-10-08 : les 7 derniers jours). */
export const DEFAULT_PERIOD_PRESET = 'last_7_days'

/**
 * Plage d'un preset, bornée à aujourd'hui.
 * @param {'today'|'yesterday'|'last_7_days'|'this_week'|'this_month'} key
 * @param {moment.Moment} [today]
 * @returns {{ from: string, to: string }}
 */
export function presetRange(key, today = moment()) {
    const end = today.clone().startOf('day')
    let start
    switch (key) {
        case 'yesterday':
            start = end.clone().subtract(1, 'day')
            return { from: toDateString(start), to: toDateString(start) }
        case 'last_7_days':
            // 7 jours calendaires, aujourd'hui compris
            start = end.clone().subtract(6, 'days')
            break
        case 'this_week':
            start = end.clone().isoWeekday(1)
            break
        case 'this_month':
            start = end.clone().startOf('month')
            break
        case 'today':
        default:
            start = end.clone()
    }
    return { from: toDateString(start), to: toDateString(end) }
}

/**
 * Preset correspondant à une plage, ou null.
 * @param {{ from: string, to: string }} range
 * @returns {string|null}
 */
export function matchPreset(range) {
    if (!range) return null
    const found = PERIOD_PRESETS.find((key) => {
        const preset = presetRange(key)
        return preset.from === range.from && preset.to === range.to
    })
    return found || null
}

// ── Mémorisation du dernier choix de période (navigateur) ──────────────────────────────────
// Demande du 2026-10-08 : la page rouvre sur la dernière période choisie par l'utilisateur ;
// « 7 derniers jours » n'est que le défaut de la première visite. Un preset est mémorisé par sa
// clé (il « glisse » avec le calendrier : « Hier » reste hier demain), une plage libre par ses dates.

const PERIOD_STORAGE_PREFIX = 'playpro.stock_movements.period'

/** Clé par module et par utilisateur (`window.User` = pont d'identité du BO). */
export function periodStorageKey(scope) {
    const userId = typeof window !== 'undefined' && window.User && window.User.id ? window.User.id : 'anon'
    return `${PERIOD_STORAGE_PREFIX}:${scope || 'default'}:${userId}`
}

function isDateString(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && moment(value, DATE_FORMAT, true).isValid()
}

/**
 * Mémorise la période appliquée. Silencieux si le stockage est indisponible (navigation privée…).
 * @param {string} key
 * @param {{ from: string, to: string }} range
 */
export function rememberPeriod(key, range) {
    if (!range || !isDateString(range.from)) return
    const preset = matchPreset(range)
    const payload = preset ? { preset } : { from: range.from, to: range.to || range.from }
    try {
        window.localStorage.setItem(key, JSON.stringify(payload))
    } catch (error) {
        // Stockage indisponible : la préférence ne survit pas au rechargement, rien de plus.
    }
}

/**
 * Dernière période mémorisée, sinon le preset par défaut. Une plage mémorisée qui ne tient plus
 * (dates invalides, futur) est ignorée.
 * @param {string} key
 * @param {string} [fallbackPreset]
 * @returns {{ from: string, to: string }}
 */
export function restorePeriod(key, fallbackPreset = DEFAULT_PERIOD_PRESET) {
    try {
        const saved = JSON.parse(window.localStorage.getItem(key) || 'null')
        if (saved && PERIOD_PRESETS.includes(saved.preset)) {
            return presetRange(saved.preset)
        }
        const today = toDateString(moment())
        if (saved && isDateString(saved.from) && isDateString(saved.to) && saved.from <= saved.to && saved.to <= today) {
            return { from: saved.from, to: saved.to }
        }
    } catch (error) {
        // Valeur illisible ou stockage indisponible : on repart du défaut.
    }
    return presetRange(fallbackPreset)
}
